import * as React from "react";
import {
    IAppointmentHistory,
    IACLEmail,
    DateFormats,
} from "intake_shared/src/types";
import moment from "../../../../../../utils/app_moment";
import { Button } from "../../../../bits/Button/Button";
import { TextArea } from "../../../../bits/TextArea/TextArea";
import { findLabelByPracticeId, toTitleCase } from "../../../../../../utils/utils";
import styles from "../AppointmentHistory.module.scss";
import { Badge } from "../../../../bits/Badge/Badge";
import { getV2BadgePropsByPractice } from "../../../../../../constants";
import ChevronUpIcon from "../../../../icons/ChevronUp";
import ChevronDownIcon from "../../../../icons/ChevronDown";
import EditIcon from "../../../../icons/Edit";
import { cn } from "../../../../../helpers";
import ServiceIcon from "../../../../icons/Service";
import ClockIcon from "../../../../icons/Clock";
import LocationIcon from "../../../../icons/Location";
import { InfoIcon } from "../../../../icons/Info";

// DetailItem component
interface DetailItemProps {
    label: string;
    value: string | React.ReactNode;
    className?: string;
}

const DetailItem: React.FC<DetailItemProps> = ({ label, value, className }) => {
    return (
        <div className={cn(styles.detailGroup)}>
            <span className={styles.label}>{label}</span>
            <span className={styles.value}>{value}</span>
        </div>
    );
};

const canEditCancellationNotes = (
    cancelledBy: string | undefined,
    loggedInUserEmail: string | undefined,
    userAcls: { [email: string]: IACLEmail | undefined }
): boolean => {
    if (!cancelledBy || !loggedInUserEmail) return false;

    const userAcl = userAcls[cancelledBy]?.acl;
    return cancelledBy === loggedInUserEmail && userAcl?.intake === true;
};

interface AppointmentCardProps {
    appointment: IAppointmentHistory;
    viewFrom: "side-bar" | "full-page";
    index: number;
    isExpanded: boolean;
    onToggleExpand: () => void;
    editMode: { [id: string]: boolean };
    editedNotes: { [id: string]: string };
    onEditClick: (id: string, currentNotes: string) => void;
    onCancelEdit: (id: string) => void;
    onSaveClick: (id: string) => Promise<void>;
    onInputChange: (id: string, value: string) => void;
    loading: { [id: string]: boolean };
    loggedInUserEmail?: string;
    userAcls: { [email: string]: IACLEmail | undefined };
    allDepartmentsAndServices: any;
    clinicianCredentialById: Record<string, string>;
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({
    appointment,
    viewFrom = "side-bar",
    index,
    isExpanded,
    onToggleExpand,
    editMode,
    editedNotes,
    onEditClick,
    onCancelEdit,
    onSaveClick,
    onInputChange,
    loading,
    loggedInUserEmail,
    userAcls,
    allDepartmentsAndServices,
    clinicianCredentialById,
}) => {
    const formatDate = (date: string) =>
        moment(date).format(DateFormats.DAY_MONTH_FORMAT);
    const formatTime = (date: string) =>
        moment(date).format(DateFormats.HOUR_MIN_FORMAT);

    const canEdit = canEditCancellationNotes(
        appointment.cancelled_by,
        loggedInUserEmail,
        userAcls
    );

    const appointmentPractice = findLabelByPracticeId(
        appointment?.practice_id ||
            (process.env.REACT_APP_MANTLE_PRACTICE_ID as string),
        allDepartmentsAndServices
    );

    const serviceDeliveryOptionLabels: { [key: string]: string } = {
        in_person: "In Person",
        telephone: "Telephone",
        video_call: "Video Call",
        fax: "Fax",
        mail: "Mail",
        other: "Other",
    };
    const clinicianCredential =
        clinicianCredentialById[appointment.clinician_id] || "";

    return viewFrom === "side-bar" ? (
        <div className={styles.appointmentCard}>
            <div className={styles.appointmentHeader}>
                <div className={styles.appointmentInfo}>
                    <div className={styles.appointmentBadges}>
                        <p className={styles.appointmentClinician}>
                            {index + 1}. {appointment.clinician_display_name}
                        </p>
                        {clinicianCredential && (
                            <Badge tone="info" content={clinicianCredential} />
                        )}
                    </div>
                    <div className={styles.appointmentBadges}>
                        <Badge
                            content={appointmentPractice}
                            textColor={
                                getV2BadgePropsByPractice(appointmentPractice)
                                    .textColor
                            }
                            bgColor={
                                getV2BadgePropsByPractice(appointmentPractice)
                                    .bgColor
                            }
                        />

                        {appointment.cancelled_on && (
                            <Badge tone="danger" content="Cancelled" />
                        )}
                    </div>
                    {appointment.cancelled_on && (
                        <DetailItem
                            label="Cancelled on:"
                            value={moment(appointment.cancelled_on).format(
                                DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                            )}
                        />
                    )}
                </div>

                {!isExpanded && (
                    <Button
                        variant="normal"
                        onClick={onToggleExpand}
                        className={styles.toggleButton}
                    >
                        View more <ChevronDownIcon stroke="#1A738D" />
                    </Button>
                )}
            </div>

            {isExpanded && (
                <div className={styles.hiddenContent}>
                    <div>
                        <DetailItem
                            label="Cancelled by:"
                            value={appointment.cancelled_by}
                        />

                        <DetailItem
                            label="Service:"
                            value={appointment.requested_service}
                        />

                        {appointment.location && (
                            <DetailItem
                                label="Location:"
                                value={appointment.location}
                            />
                        )}

                        {appointment.service_delivery_option && (
                            <DetailItem
                                label="Delivery Option:"
                                value={toTitleCase(
                                    appointment.service_delivery_option?.replace(
                                        "_",
                                        " "
                                    ) || "N/A"
                                )}
                            />
                        )}

                        <DetailItem
                            label="Starts:"
                            value={`${formatDate(appointment.start)} at ${formatTime(
                                appointment.start
                            )}`}
                        />

                        {appointment.recurrenceRule && (
                            <DetailItem
                                label="Recurrence:"
                                value={appointment.recurrenceRule}
                            />
                        )}
                    </div>

                    {appointment.cancellation_notes && (
                        <>
                            {!editMode[appointment.id] && (
                                <div className={styles.notesSection}>
                                    <div className={styles.notesContainer}>
                                        <span className={styles.notesLabel}>
                                            Cancellation notes:
                                        </span>
                                        <span className={styles.notesValue}>
                                            {appointment.cancellation_notes}
                                        </span>
                                    </div>

                                    {canEdit && (
                                        <Button
                                            variant="normal"
                                            onClick={() =>
                                                onEditClick(
                                                    appointment.id,
                                                    appointment.cancellation_notes
                                                )
                                            }
                                            className={styles.editButton}
                                        >
                                            <EditIcon />
                                        </Button>
                                    )}
                                </div>
                            )}

                            {canEdit && editMode[appointment.id] && (
                                <div className={styles.editMode}>
                                    <TextArea
                                        value={editedNotes[appointment.id] || ""}
                                        onChange={(e) =>
                                            onInputChange(
                                                appointment.id,
                                                e.target.value
                                            )
                                        }
                                        placeholder="Enter cancellation notes..."
                                    />
                                    <div className={styles.editActions}>
                                        <div>
                                            <Button
                                                onClick={() =>
                                                    onSaveClick(appointment.id)
                                                }
                                                disabled={loading[appointment.id]}
                                            >
                                                Save
                                            </Button>
                                        </div>
                                        <div>
                                            <Button
                                                variant="secondary"
                                                onClick={() =>
                                                    onCancelEdit(appointment.id)
                                                }
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    {isExpanded && (
                        <Button
                            variant="normal"
                            onClick={onToggleExpand}
                            className={styles.toggleButton}
                        >
                            View less <ChevronUpIcon stroke="#1A738D" />
                        </Button>
                    )}
                </div>
            )}
        </div>
    ) : (
        <div className={styles.apptHistoryPageContainer}>
            <div className={styles.appointmentHeader}>
                <div className={styles.clinicianInfo}>
                    <div className={styles.clinicianBadges}>
                        <p className={styles.clinicianName}>
                            {appointment.clinician_display_name}
                        </p>
                        {clinicianCredential && (
                            <Badge tone="info" content={clinicianCredential} />
                        )}
                    </div>

                    <div className={styles.clinicianBadges}>
                        <Badge
                            content={appointmentPractice}
                            textColor={
                                getV2BadgePropsByPractice(appointmentPractice)
                                    .textColor
                            }
                            bgColor={
                                getV2BadgePropsByPractice(appointmentPractice)
                                    .bgColor
                            }
                        />
                        {appointment.cancelled_on && (
                            <Badge tone="danger" content="Cancelled" />
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.detailsGrid}>
                <div className={styles.detailCard}>
                    <div className={styles.detailIcon}>
                        <ServiceIcon />
                    </div>
                    <div className={styles.detailContent}>
                        <div className={styles.detailLabel}>Service</div>
                        <div className={styles.detailValue}>
                            {appointment.requested_service}
                        </div>
                        {appointment.service_delivery_option && (
                            <div className={styles.detailSubtext}>
                                {serviceDeliveryOptionLabels[
                                    appointment.service_delivery_option || ""
                                ] ||
                                    toTitleCase(
                                        appointment.service_delivery_option?.replace(
                                            "_",
                                            " "
                                        ) || "N/A"
                                    )}
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.detailCard}>
                    <div className={styles.detailIcon}>
                        <ClockIcon />
                    </div>
                    <div className={styles.detailContent}>
                        <div className={styles.detailLabel}>Time</div>
                        <div className={styles.detailValue}>
                            {formatDate(appointment.start)} at{" "}
                            {formatTime(appointment.start)}
                        </div>
                        {appointment.recurrenceRule && (
                            <div className={styles.detailSubtext}>
                                {appointment.recurrenceRule}
                            </div>
                        )}
                    </div>
                </div>

                {appointment.location && (
                    <div className={styles.detailCard}>
                        <div className={styles.detailIcon}>
                            <LocationIcon />
                        </div>
                        <div className={styles.detailContent}>
                            <div className={styles.detailLabel}>Location</div>
                            <div className={styles.detailValue}>
                                {appointment.location}
                            </div>
                        </div>
                    </div>
                )}

                {appointment.cancelled_on && (
                    <div className={styles.detailCard}>
                        <div className={styles.detailIcon}>
                            <InfoIcon />
                        </div>
                        <div className={styles.detailContent}>
                            <div className={styles.detailLabel}>Cancelled</div>
                            <div className={styles.detailValue}>
                                {moment(appointment.cancelled_on).format(
                                    DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                                )}
                            </div>
                            <div className={styles.detailSubtext}>
                                By: {appointment.cancelled_by}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {!editMode[appointment.id] && (
                <div className={cn(styles.notesSection, styles.notesSectionPage)}>
                    <div
                        className={cn(
                            styles.notesContainer,
                            styles.notesContainerPage
                        )}
                    >
                        <span className={styles.notesLabel}>
                            Cancellation notes:
                        </span>
                        <span className={styles.notesValue}>
                            {appointment.cancellation_notes}
                        </span>
                    </div>

                    {canEdit && (
                        <Button
                            variant="normal"
                            onClick={() =>
                                onEditClick(
                                    appointment.id,
                                    appointment.cancellation_notes
                                )
                            }
                            className={styles.editButton}
                        >
                            <EditIcon />
                        </Button>
                    )}
                </div>
            )}

            {canEdit && editMode[appointment.id] && (
                <div className={styles.editMode}>
                    <TextArea
                        value={editedNotes[appointment.id] || ""}
                        onChange={(e) =>
                            onInputChange(appointment.id, e.target.value)
                        }
                        placeholder="Enter cancellation notes..."
                    />
                    <div className={styles.editActions}>
                        <div>
                            <Button
                                onClick={() => onSaveClick(appointment.id)}
                                disabled={loading[appointment.id]}
                            >
                                Save
                            </Button>
                        </div>
                        <div>
                            <Button
                                variant="secondary"
                                onClick={() => onCancelEdit(appointment.id)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AppointmentCard;
